<template>
  <div
    :id="containerId"
    ref="root"
    class="filterable-select"
    @click.stop
  >
    <div class="filterable-select__input">
      <ul
        v-if="showChips"
        ref="chips"
        :class="{ ground: isFocused || query }"
        class="filterable-select__chips"
        @click.stop
      >
        <li
          v-for="chip in selectedChips"
          :key="chip.value"
          class="chip is-system"
        >
          <span @click.prevent="focus">{{ chip.name }}</span>
          <button
            type="button"
            class="chip__remove"
            @click.prevent="removeChip(chip.value)"
          >
            <i class="fas fa-times" />
          </button>
        </li>
      </ul>

      <div class="filterable-select__field">
        <span
          v-if="searching"
          :class="{ ground: !isFocused || !query }"
          class="icon"
        >
          <i class="fas fa-spinner" />
        </span>

        <button
          v-else-if="showClearButton && !disabled"
          type="button"
          :class="{ ground: !isFocused || !query }"
          class="icon"
          :title="clearLabel"
          @click.prevent="clearSelection"
        >
          <i class="far fa-times-circle" />
        </button>

        <input
          ref="query"
          v-model="query"
          v-bind="$attrs"
          :placeholder="effectivePlaceholder"
          :disabled="searching || disabled"
          :[tabindexAttr]="tabindex"
          autocomplete="off"
          class="input"
          type="text"
          @focus="focus"
          @change="$emit('change')"
          @keydown.enter="onInputKeydown"
          @keyup.enter.prevent.stop="handleEnter"
        >

        <span
          v-if="!showClearButton && !searching"
          class="icon has-text-link"
          @click="focus"
        >
          <i class="fas fa-chevron-down" />
        </span>
      </div>
    </div>

    <button
      v-if="showAddButton"
      type="button"
      class="button filterable-select__add"
      :title="addLabel"
      @click.prevent="emitAdd"
    >
      <i class="fas fa-plus" />
    </button>

    <div
      v-if="showList"
      class="filterable-select__list"
    >
      <div class="filterable-select__list-content">
        <template v-if="canMultiselect">
          <label
            v-for="option in filteredOptions"
            :key="`multi-${option.value}`"
            class="checkbox button is-ghost"
          >
            <i
              class="mr-2"
              :class="isOptionChecked(option) ? 'fas fa-check-square' : 'far fa-square'"
            />
            <input
              v-model="multiValue"
              :value="option.value"
              type="checkbox"
              @click="emitInput()"
            >
            <span>{{ option.name }}</span>
          </label>
        </template>

        <template v-if="!canMultiselect">
          <button
            v-for="option in filteredOptions"
            :key="`single-${option.value}`"
            type="button"
            :title="option.name"
            class="button is-ghost"
            @click.prevent="selectOption(option)"
          >
            {{ option.name }}
          </button>
        </template>

        <span
          v-if="!filteredOptions.length"
          class="filterable-select__empty"
        >
          {{ emptyLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { clickOutsideMixin } from './clickOutsideMixin';
import { filterOptions, findOptionByValue, nextSelectId } from './helpers';

const TYPING_DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 2;

export default {
  name: 'FilterableSelect',
  mixins: [clickOutsideMixin],
  inheritAttrs: false,
  props: {
    value: { type: [String, Number, Array, Boolean], default: '' },
    options: { type: Array, required: true },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    searching: { type: Boolean, default: false },
    canAdd: { type: Boolean, default: false },
    canMultiselect: { type: Boolean, default: false },
    hasApiSearch: { type: Boolean, default: false },
    tabindex: { type: [Number, String], default: null },
    clearLabel: { type: String, default: 'Limpar seleção' },
    addLabel: { type: String, default: 'Adicionar' },
    emptyLabel: { type: String, default: 'Nenhuma opção encontrada' },
    focusOnLoad: { type: Boolean, default: true },
  },
  data() {
    return {
      containerId: nextSelectId(),
      query: '',
      isTyping: false,
      typingTimer: null,
    };
  },
  computed: {
    multiValue: {
      get() {
        return Array.isArray(this.value)
          ? this.value
          : [];
      },
      set(next) {
        this.$emit('input', next);
      },
    },
    selectedOptionName() {
      if (this.canMultiselect) return '';
      const match = findOptionByValue(this.options, this.value);
      return match?.name ?? '';
    },
    filteredOptions() {
      return filterOptions(this.options, this.query);
    },
    selectedChips() {
      if (!Array.isArray(this.value)) return [];
      return this.value
        .map((val) => findOptionByValue(this.options, val))
        .filter(Boolean);
    },
    showChips() {
      return this.canMultiselect && this.selectedChips.length > 0;
    },
    showList() {
      return this.isFocused
        && this.options.length > 0
        && !this.isTyping
        && !this.searching;
    },
    showClearButton() {
      if (this.canMultiselect) return this.selectedChips.length > 0;
      return Boolean(this.value);
    },
    showAddButton() {
      return this.canAdd
        && Boolean(this.query)
        && this.filteredOptions.length === 0
        && !this.isTyping
        && !this.searching;
    },
    effectivePlaceholder() {
      return this.showChips ? '' : this.placeholder;
    },
    tabindexAttr() {
      return this.tabindex != null ? 'tabindex' : null;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() { this.syncQueryFromValue(); },
    },
    query(next, prev) {
      this.isTyping = true;
      clearTimeout(this.typingTimer);

      this.typingTimer = setTimeout(() => {
        this.isTyping = false;
        if (next === prev) return;
        if (next && next.length < MIN_QUERY_LENGTH) return;
        if (!this.isFocused) return;
        this.$emit('search', next);
      }, TYPING_DEBOUNCE_MS);
    },
    searching(value) {
      if (!value && this.focusOnLoad) {
        this.$nextTick(() => this.$refs.query?.focus());
      }
    },
  },
  mounted() {
    if (this.hasApiSearch && this.canAdd) {
      console.warn('[FilterableSelect] hasApiSearch and canAdd cannot be used together.');
    }
  },
  beforeDestroy() {
    clearTimeout(this.typingTimer);
  },
  methods: {
    focus() {
      const wasUnfocused = !this.isFocused;
      this.isFocused = true;
      this.$emit('focus');

      if (wasUnfocused && this.query && this.query === this.selectedOptionName) {
        this.$nextTick(() => this.$refs.query?.select());
      }
    },
    emitInput(payload) {
      this.$emit('input', payload ?? this.value);
    },
    emitAdd() {
      this.$emit('added', this.query);
    },
    selectOption(option) {
      this.emitInput(option.value);
      this.isFocused = false;
      this.$emit('blur');
    },
    clearSelection() {
      const emptyValue = this.canMultiselect ? [] : '';
      this.query = '';
      this.emitInput(emptyValue);
    },
    removeChip(value) {
      if (!Array.isArray(this.value)) return;
      this.emitInput(this.value.filter((item) => item !== value));
    },
    isOptionChecked(option) {
      return Array.isArray(this.value) && this.value.includes(option.value);
    },
    onInputKeydown(event) {
      if (this.showList) event.stopPropagation();
    },
    handleEnter() {
      if (this.showAddButton) this.emitAdd();
    },
    syncQueryFromValue() {
      if (this.canMultiselect) return;
      const match = findOptionByValue(this.options, this.value);
      this.query = match?.name ?? '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_checkbox.scss';
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_fieldSignature.scss';

.filterable-select {
  position: relative;
  display: flex;

  &__input {
    display: grid;
    width: 100%;
  }

  &__field {
    position: relative;

    input.input {
      @include field-frame;
      @include field-text;
      @include field-focus;
      width: 100%;
      display: block;
      transition: all .15s ease;

      &:disabled { border: #D1D5DB solid 1px; }
    }

    .icon {
      position: absolute;
      top: 50%;
      right: calc(1.25rem - 12px + 3.5px);
      z-index: 2;
      background-color: transparent;
      padding: 7px;
      border: none;
      font-size: 1.25em;
      cursor: pointer;
      transform: translateY(-50%);

      &.ground {
        top: calc(1.25rem - 12px);
        transform: initial;
      }

      .fa-spinner {
        animation: rotate 1.25s ease-in-out infinite;
      }

      @keyframes rotate {
        100% { transform: rotate(360deg); }
      }
    }
  }

  &__add {
    margin-top: auto;
    min-width: 45px;
    border: none;
  }

  &__list {
    position: absolute;
    top: calc(100% + 5px);
    left: 0;
    z-index: 10;
    width: 100%;
    max-height: 300px;
    background-color: #fff;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    box-shadow: 0 2px 5px rgba(#000, 0.05);
    overflow-x: auto;
    transition: all .05s ease;
  }

  &__list-content {
    display: grid;
    width: 100%;
    padding: 10px;
    grid-template-columns: 1fr;
    row-gap: 10px;
    scroll-snap-type: y mandatory;

    .button,
    .checkbox {
      display: block;
      min-width: fit-content;
      background-color: #fff;
      color: $color-secondary;
      text-overflow: ellipsis;
      text-align: left;
      text-decoration: none;
      overflow: hidden;
      justify-content: flex-start;
      scroll-snap-align: start;

      &:hover {
        box-shadow: 0 0 0 0.15em rgba(var(--color-primary-rgb), 0.5);
      }
      &:focus {
        background-color: rgba(var(--color-primary-rgb), 0.75);
        color: #fff;
      }
    }
  }

  &__empty {
    color: #666;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    column-gap: 5px;
    row-gap: 5px;
    margin-bottom: 5px;
    min-height: 26px;
  }
}

.chip {
  @include reset_checkbox;

  display: flex;
  align-items: center;
  border-radius: 3px;
  padding-left: 0.5rem;
  border: none;
  background-color: $slate-200;
  color: #172b4d;
  font-size: .85rem;
  font-weight: 500;
  white-space: nowrap;

  span {
    display: inline-block;
    max-width: 85%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__remove {
    background: none;
    border: 0;
    padding: 0.5rem;
    margin-left: auto;
    cursor: pointer;

    &:hover { background-color: #9CA3AF; }
  }
}
</style>
